import { useNavigate } from '@tanstack/react-router'
import { CookieStorage } from 'cookie-storage'
import { User, UserManager, WebStorageStateStore } from 'oidc-client-ts'
import React, { useCallback, useEffect, useState } from 'react'

import {
  CognitoAuthenticatedContent,
  CognitoMessageBanner,
} from './CognitoLoginScreen'

const CognitoContext = React.createContext<ICognitoContext>(
  {} as ICognitoContext
)
export const useCognito = () => {
  const context = React.useContext(CognitoContext)
  return context
}

type ICognitoContext = {
  isAuthenticated: boolean
  isLoading: boolean
  error: Error | null
  user: User | null
  sendToCognitoLogin: (scopes?: string[]) => void
  signOut: () => void
  /* Function that attempts to exchange the currently stored refresh token for a fresh acceess token.
   * Returns a promise that is fulfilled the refresh was successful, rejected if unsuccesful, and undefined if the
   * global user manage has not been instantiated yet*/
  exchangeRefreshToken: () => Promise<void> | undefined
}

type CognitoProviderOptions = {
  children: React.JSX.Element
  clientId: string
  cognitoUserPoolId: string
  awsRegion: string
  baseDomain: string
  basePath?: string
}

type AppState = {
  redirectLocation?: string
}

// We store this in a global to prevent multiple user managers from being
// created simultaneously, which causes problems.
let globalCognitoUserManager: UserManager | undefined = undefined

export const CognitoProvider = ({
  children,
  awsRegion,
  clientId,
  cognitoUserPoolId,
  baseDomain,
  basePath = '',
  ...rest
}: CognitoProviderOptions) => {
  const [user, setUser] = useState<User | null>(null)
  const [error, setError] = useState<Error | null>(null)
  // `isLoading` should be true while we're handling callback url params. It
  // should only be set to false once we're done. We rely on this in the
  // `CognitoAuthenticatedContent` component, which can do the wrong thing if
  // `isLoading` is set to false without `isAuthenticated` being set properly.
  const [isLoading, setIsLoading] = useState<boolean>(true)
  // TODO: we can remove the cookie-storage library once we remove cognito
  const [cookieStore] = useState<CookieStorage>(
    new CookieStorage({
      expires: new Date(Date.now() + 60 * 60 * 1000),
    })
  )

  const navigate = useNavigate()

  useEffect(() => {
    // Only initialize the user manager once. If we run this multiple times, we
    // end up with concurrently running user managers and bizarre problems
    if (globalCognitoUserManager) {
      return
    }
    globalCognitoUserManager = new UserManager({
      authority: `https://login.${baseDomain}`,
      client_id: clientId,
      redirect_uri: window.location.origin + basePath + '/auth/callback',
      metadataUrl: `https://cognito-idp.${awsRegion}.amazonaws.com/${cognitoUserPoolId}/.well-known/openid-configuration`,
      response_mode: 'query',
      userStore: new WebStorageStateStore({ store: window.localStorage }),
      stateStore: new WebStorageStateStore({ store: cookieStore }),
      automaticSilentRenew: false,
      ...rest,
    })
    globalCognitoUserManager.events.addUserLoaded((user) => {
      setUser(user)
    })

    globalCognitoUserManager.events.addUserUnloaded(() => {
      setUser(null)
    })

    if (window.location.pathname === `${basePath}/auth/callback`) {
      // If the current path is the callback, process and redirect back to the original location
      globalCognitoUserManager
        .signinRedirectCallback(window.location.href)
        .then((user) => {
          const state = user.state as AppState
          navigate({ to: state.redirectLocation ?? '/' })
          globalCognitoUserManager?.storeUser(user)
        })
        .catch((err) => {
          setError(err)
        })
        .finally(() => {
          setIsLoading(false)
        })
    } else if (window.location.pathname === `${basePath}/auth/logout`) {
      setIsLoading(true)
      globalCognitoUserManager
        .signoutCallback()
        .then(() => {
          globalCognitoUserManager?.removeUser()
          navigate({ to: '/' })
        })
        .catch((err) => {
          setError(err)
        })
        .finally(() => {
          setIsLoading(false)
        })
    } else {
      globalCognitoUserManager.getUser().then((user: User | null) => {
        if (user) {
          setUser(user)
        }
        setIsLoading(false)
      })
    }
  }, [
    basePath,
    cookieStore,
    navigate,
    baseDomain,
    clientId,
    awsRegion,
    cognitoUserPoolId,
    rest,
  ])

  const sendToCognitoLogin = async (
    scopes: string[] = ['email', 'openid', 'profile']
  ) => {
    setIsLoading(true)

    globalCognitoUserManager
      ?.signinRedirect({
        scope: scopes.join(' '),
        redirect_uri: window.location.origin + `${basePath}/auth/callback`,
        state: JSON.stringify({ redirectLocation: window.location.href }),
      })
      .catch((err) => {
        console.error(err)
        setError(err)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const exchangeRefreshToken = useCallback(
    () =>
      globalCognitoUserManager
        ?.signinSilent()
        .then((newUser) => setUser(newUser)),
    []
  )

  const signOut = async () => {
    setIsLoading(true)

    globalCognitoUserManager
      ?.signoutRedirect({
        extraQueryParams: {
          client_id: clientId,
          logout_uri: `${window.location.origin}${basePath}/auth/logout`,
        },
      })
      .catch((err) => {
        console.error(err)
        setError(err)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  return (
    <CognitoContext.Provider
      value={{
        isAuthenticated: !!user?.access_token,
        user,
        error,
        isLoading,
        signOut,
        sendToCognitoLogin,
        exchangeRefreshToken,
      }}>
      <CognitoAuthenticatedContent>{children}</CognitoAuthenticatedContent>
      <CognitoMessageBanner />
    </CognitoContext.Provider>
  )
}
